import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, MenuItem, TextField } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import IntelLayout from './components/IntelLayout'
import KpiCard from './components/KpiCard'
import Badge from './components/Badge'
import IntelTable from './components/IntelTable'
import { intelColors, formatNumber } from './components/intelTheme'
import { intelApi } from './api'
import { ChartCard, Bars, Donut } from './components/IntelCharts'

const THRESHOLDS = [
  { value: 30, label: '≥ 30%' },
  { value: 40, label: '≥ 40%' },
  { value: 50, label: '≥ 50%' },
]
const SUGGESTED = {
  WATCH: 'Monitor — review trend next week before blocking',
  HIGH: 'Block COD; consider Prepaid-only routing',
  CRITICAL: 'Full block all modes — escalate to courier BD',
}
const impactColor = (key) => (key === 'CRITICAL' ? 'red' : key === 'HIGH' || key === 'WATCH' ? 'orange' : 'green')
const inr = (v) => '₹' + formatNumber(Math.round(v || 0))

// ── client-side CSV ──
const csvEsc = (v) => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}
const downloadCsv = (filename, headers, rows) => {
  const body = [headers.map((h) => csvEsc(h.label)).join(',')]
  rows.forEach((r) => body.push(headers.map((h) => csvEsc(h.get(r))).join(',')))
  const blob = new Blob([body.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
const couriersStr = (r) => (r.topCouriers || []).map((c) => `${c.name} (${c.rto})`).join('; ')
const PIN_CSV = [
  { label: 'Pincode', get: (r) => r.pincode },
  { label: 'City', get: (r) => r.city },
  { label: 'RTO %', get: (r) => r.rtoRate },
  { label: 'Orders (30d)', get: (r) => r.orders },
  { label: "Orders RTO'd", get: (r) => r.ordersRtod },
  { label: 'GMV at Risk', get: (r) => Math.round(r.gmvRisk || 0) },
  { label: 'Top Couriers (by RTO)', get: couriersStr },
]

// Aggregate top courier shares within a bucket (from each pincode's topCouriers).
const bucketTopCouriers = (b) => {
  const agg = {}
  ;(b.rows || []).forEach((r) => (r.topCouriers || []).forEach((c) => { agg[c.name] = (agg[c.name] || 0) + (c.rto || 0) }))
  const total = Object.values(agg).reduce((s, v) => s + v, 0)
  if (!total) return '—'
  return Object.entries(agg).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([n, v]) => `${n} (${Math.round((v / total) * 100)}%)`).join(', ')
}

export default function BlockRto() {
  const navigate = useNavigate()
  const [threshold, setThreshold] = useState(30)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [data, setData] = useState(null)

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setErr(null)
    intelApi
      .blockRto({ threshold })
      .then((res) => {
        if (cancel) return
        setData(res?.data?.responseObj || res?.data?.data || res?.data)
      })
      .catch((e) => !cancel && setErr(e?.response?.data?.exception || e.message))
      .finally(() => !cancel && setLoading(false))
    return () => {
      cancel = true
    }
  }, [threshold])

  const buckets = useMemo(() => data?.buckets || [], [data])
  const courierImpact = useMemo(() => data?.courierImpact || [], [data])
  const totalRtod = useMemo(() => courierImpact.reduce((s, c) => s + (c.ordersRtod || 0), 0), [courierImpact])
  const flaggedTotal = data?.flaggedPincodes || 0

  const impactBars = useMemo(
    () => courierImpact.slice(0, 12).map((c) => ({ name: c.name, value: c.rtoPct || 0 })),
    [courierImpact]
  )
  const bucketDonut = useMemo(
    () => buckets.filter((b) => b.pincodeCount > 0).map((b) => ({ name: b.key, value: b.pincodeCount })),
    [buckets]
  )
  const allFlaggedRows = useMemo(() => buckets.flatMap((b) => (b.rows || []).map((r) => ({ ...r, bucket: b.key }))), [buckets])

  const impactCols = [
    { key: 'name', header: 'Parent Courier', render: (r) => <strong>{r.name}</strong> },
    { key: 'ordersRtod', header: "RTO'd Orders", render: (r) => formatNumber(r.ordersRtod) },
    { key: 'pctTotal', header: '% of Total RTOs', render: (r) => (totalRtod ? <Badge color={impactColor(r.bucket)}>{((r.ordersRtod / totalRtod) * 100).toFixed(1)}%</Badge> : '—') },
    { key: 'pincodes', header: 'Pincodes Involved', render: (r) => `${formatNumber(r.pincodes)} / ${formatNumber(flaggedTotal)} PINs` },
    { key: 'mostCommonBucket', header: 'Most Common Bucket', render: (r) => (r.mostCommonBucket ? <Badge color={impactColor(r.mostCommonBucket)}>{r.mostCommonBucket}</Badge> : '—') },
    { key: 'avgRtoRate', header: 'Avg RTO Rate (PINs)', render: (r) => (r.avgRtoRate != null ? `${r.avgRtoRate}%` : '—') },
    { key: 'recommendation', header: 'Recommendation', render: (r) => r.recommendation || '—' },
  ]

  const columns = [
    { key: 'pincode', header: 'Pincode', render: (r) => <strong>{r.pincode}</strong> },
    { key: 'city', header: 'City', render: (r) => r.city || '—' },
    { key: 'rtoRate', header: 'RTO %', render: (r) => <Badge color='red'>{r.rtoRate}%</Badge> },
    { key: 'orders', header: 'Orders (30d)', render: (r) => formatNumber(r.orders) },
    { key: 'ordersRtod', header: "Orders RTO'd", render: (r) => formatNumber(r.ordersRtod) },
    { key: 'gmvRisk', header: 'GMV at Risk', render: (r) => inr(r.gmvRisk) },
    { key: 'topCouriers', header: 'Top Couriers (by RTO)', render: (r) => couriersStr(r) || '—' },
    {
      key: 'action',
      header: 'Action',
      render: () => (
        <Button size='small' color='error' variant='outlined' onClick={() => navigate('/serviceability-intel/block/new')}>
          Block
        </Button>
      ),
    },
  ]

  return (
    <IntelLayout title='Block Pincodes — RTO Block Suggestions'>
      <Box sx={{ background: '#e8f4fd', border: '1px solid #b3d9f2', borderRadius: '6px', p: '10px 14px', mb: 2, fontSize: 12, color: '#1565c0' }}>
        Drop pincodes with elevated RTO over the trailing 30 days, bucketed by severity. Each bucket lists the pincodes,
        RTO'd orders, GMV at risk, and the courier partners most involved — so ops can decide to block, restrict COD, or escalate.
        RTO% = RTO'd orders ÷ total forward shipped orders, per pincode. Only pincodes with ≥ 5 shipped orders are flagged.
      </Box>
      <Box sx={{ background: '#fff', border: `1px solid ${intelColors.border}`, borderRadius: '8px', p: '14px 18px', mb: 2, display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <TextField select label='RTO Threshold' value={threshold} onChange={(e) => setThreshold(Number(e.target.value))} size='small' sx={{ minWidth: 140 }}>
          {THRESHOLDS.map((o) => (
            <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        <Button
          size='small'
          variant='outlined'
          disabled={allFlaggedRows.length === 0}
          onClick={() => downloadCsv('rto_flagged_pincodes.csv', [{ label: 'Bucket', get: (r) => r.bucket }, ...PIN_CSV], allFlaggedRows)}
        >
          ⬇️ All flagged PINs (CSV)
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', mb: 2 }}>
        <KpiCard loading={loading} color='red' label='Flagged Pincodes (30d)' value={formatNumber(data?.flaggedPincodes)} sub={`RTO ≥ ${data?.threshold || threshold}% · min 5 orders`} />
        <KpiCard loading={loading} color='orange' label='Orders Impacted (30d)' value={formatNumber(data?.ordersImpacted)} sub="RTO'd orders, flagged PINs" />
        <KpiCard loading={loading} color='purple' label='GMV at Risk (30d)' value={inr(data?.gmvAtRisk)} sub="Value of RTO'd orders" />
        <KpiCard loading={loading} color='blue' label='Couriers Involved' value={data ? `${formatNumber(data.couriersInvolved)} / ${formatNumber(data.totalCourierParents)}` : '—'} sub='Parents in flagged PINs' />
      </Box>

      {err && (
        <Box sx={{ background: '#ffebee', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', p: '10px 14px', mb: '14px', fontSize: 12 }}>{err}</Box>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Box component='span' sx={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>RTO Buckets:</Box>
        <Badge color='orange'>🟡 30–40% · Watch</Badge>
        <Badge color='orange'>🟠 40–50% · High Risk</Badge>
        <Badge color='red'>🔴 &gt;50% · Critical</Badge>
      </Box>

      {(impactBars.length > 0 || bucketDonut.length > 0) && (
        <Box sx={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '14px', mb: 2 }}>
          <ChartCard loading={loading} title='Courier RTO % (across flagged pincodes)'>
            <Bars data={impactBars} color='#c62828' height={250} />
          </ChartCard>
          <ChartCard loading={loading} title='Flagged Pincodes by Severity'>
            <Donut data={bucketDonut} height={250} />
          </ChartCard>
        </Box>
      )}

      {buckets.map((b) => (
        <Box key={b.key} sx={{ mb: 2.5 }}>
          {/* summary strip */}
          <Box sx={{ background: '#f8f9fa', border: `1px solid ${intelColors.border}`, borderBottom: 'none', borderRadius: '8px 8px 0 0', p: '8px 14px', display: 'flex', gap: 3, flexWrap: 'wrap', fontSize: 12, color: '#555' }}>
            <span><strong style={{ color: '#37474f' }}>Orders impacted:</strong> {formatNumber(b.ordersImpacted)}</span>
            <span><strong style={{ color: '#37474f' }}>GMV at risk:</strong> {inr(b.gmvRisk)}</span>
            <span><strong style={{ color: '#37474f' }}>Top couriers:</strong> {bucketTopCouriers(b)}</span>
            <span><strong style={{ color: '#666' }}>Suggested:</strong> {SUGGESTED[b.key] || '—'}</span>
          </Box>
          <IntelTable
            title={`${b.label}  —  ${formatNumber(b.pincodeCount)} pincodes`}
            action={
              <Button size='small' variant='text' disabled={!(b.rows || []).length} onClick={() => downloadCsv(`rto_${b.key.toLowerCase()}.csv`, PIN_CSV, b.rows || [])}>
                ⬇️ CSV
              </Button>
            }
            loading={loading}
            columns={columns}
            rows={b.rows || []}
            rowKey={(r, i) => r.pincode || i}
            pageSize={25}
            emptyMsg='No pincodes in this bucket'
          />
        </Box>
      ))}

      <IntelTable
        title='Couriers Most Involved in Flagged Pincodes — Aggregate'
        action={
          <Button size='small' variant='text' disabled={!courierImpact.length} onClick={() => downloadCsv('rto_courier_impact.csv', [
            { label: 'Parent Courier', get: (r) => r.name },
            { label: "RTO'd Orders", get: (r) => r.ordersRtod },
            { label: '% of Total RTOs', get: (r) => (totalRtod ? ((r.ordersRtod / totalRtod) * 100).toFixed(1) : '') },
            { label: 'Pincodes Involved', get: (r) => r.pincodes },
            { label: 'Most Common Bucket', get: (r) => r.mostCommonBucket },
            { label: 'Avg RTO Rate', get: (r) => r.avgRtoRate },
            { label: 'Recommendation', get: (r) => r.recommendation },
          ], courierImpact)}>
            ⬇️ CSV
          </Button>
        }
        loading={loading}
        columns={impactCols}
        rows={courierImpact}
        rowKey={(r, i) => r.name || i}
        pageSize={25}
        emptyMsg='No courier impact (no flagged pincodes)'
      />

      <Box sx={{ background: '#f8faff', border: '1px solid #d0d7de', borderRadius: '6px', p: '12px 16px', mt: 0.5, fontSize: 11.5, color: '#555', lineHeight: 1.7 }}>
        <strong>How this works:</strong> RTO rate = <code>RTO'd orders ÷ total forward shipped orders × 100</code>, per pincode over a rolling 30-day window.
        Pincodes are bucketed by severity (30–40% Watch, 40–50% High Risk, &gt;50% Critical) and must have ≥ 5 shipped orders to flag.
        "Top Couriers" shows the parent couriers contributing most RTOs at each pincode. "Avg RTO Rate" in the courier table is the mean of the
        pincode-level rates where that courier appears; "Most Common Bucket" is the modal severity of those pincodes.
        <br /><br />
        <strong>Actions</strong> (block / restrict COD / escalate) are handled in the Block Pincodes module — this page is read-only analytics.
      </Box>
    </IntelLayout>
  )
}
